// "Refill sheet…" — the upload dialog (group-home roster refills, PR-E; O154 F5, O169).
//
//   1. the facility: the practice's group homes that have Elation tags, each with how many
//      charts its tags put in scope (pre-selected when there is only one);
//   2. the sheet: a file input (photos or one PDF — never image/heic in `accept`, O169),
//      a drop zone, or a paste; each photo is made upright and small enough in the
//      browser first (lib/image-resize), a PDF goes as it is;
//   3. upload → POST /api/sms/chat/roster → the sheet appears in the chat and is polled.
// It also lists the provider's recent sheets: the way back to one after a reload.
import React, { useEffect, useRef, useState } from 'react';
import '../../styles/refill-sheet.css';
import { ccmApi } from '../../lib/api.js';
import { SheetModal } from './sheet-modal.jsx';
import {
  ROSTER_ACCEPT, chartsInScopeText, progressStepOf, uploadReadiness, performUpload,
} from '../../lib/refill-sheet.js';
import {
  prepareFile, bytesToBase64, domDeps, normalizeLimits,
} from '../../lib/image-resize.js';

let fileSeq = 0;

// The facility pick: a radio group, so it is one Tab stop with arrow keys inside.
export function FacilityPick({ facilities, value, onChange, loading = false, error = null }) {
  if (loading) return <div className="rs-muted" role="status">Loading group homes…</div>;
  if (error) return <div className="rs-error" role="alert">{error}</div>;
  if (!facilities || !facilities.length) {
    return (
      <div className="rs-empty" role="note">
        No group home is set up for refill sheets yet. An admin adds a home's Elation tags in
        Kiosk admin → Facilities.
      </div>
    );
  }
  return (
    <fieldset className="rs-facilities">
      <legend>Which group home is this sheet from?</legend>
      {facilities.map((f) => (
        <label key={f.id} className={`rs-facility${value === f.id ? ' is-selected' : ''}`}>
          <input
            type="radio"
            name="rs-facility"
            value={f.id}
            checked={value === f.id}
            onChange={() => onChange && onChange(f.id)}
          />
          <span className="rs-facility-name">{f.name}</span>
          <span className="rs-facility-scope">{chartsInScopeText(f.residentCharts)}</span>
        </label>
      ))}
    </fieldset>
  );
}

// The sheet's files: the input, the drop zone, and what was added (each with its own
// state while it is prepared).
export function SheetFiles({ files, onAdd, onRemove, dragOver = false, setDragOver, inputRef, maxPages = 4 }) {
  const hintId = 'rs-files-hint';
  return (
    <div className="rs-files">
      <div
        className={`rs-drop${dragOver ? ' is-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); if (setDragOver) setDragOver(true); }}
        onDragLeave={() => setDragOver && setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          if (setDragOver) setDragOver(false);
          const list = e.dataTransfer && e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
          if (list.length && onAdd) onAdd(list);
        }}
      >
        <label className="rs-file-label">
          <span className="btn primary sm">Choose photos or a PDF</span>
          <input
            ref={inputRef}
            className="rs-file-input"
            type="file"
            accept={ROSTER_ACCEPT}
            multiple
            aria-describedby={hintId}
            onChange={(e) => {
              const list = e.target.files ? Array.from(e.target.files) : [];
              e.target.value = '';
              if (list.length && onAdd) onAdd(list);
            }}
          />
        </label>
        <div id={hintId} className="rs-drop-hint">
          or drop them here, or paste a photo. Up to {maxPages} photos (one per page) or one PDF.
          Large photos are made smaller here before they are sent.
        </div>
      </div>
      {files.length > 0 && (
        <ul className="rs-file-list" aria-label="Pages to upload">
          {files.map((f, i) => (
            <li key={f.id} className={`rs-file rs-file-${f.status}`}>
              <span className="rs-file-name">{f.kind === 'pdf' ? 'PDF' : `Page ${i + 1}`} · {f.name}</span>
              <span className="rs-file-state" role={f.status === 'error' ? 'alert' : undefined}>
                {f.status === 'preparing' && 'Getting it ready…'}
                {f.status === 'ready' && (f.resized ? `Ready (made smaller: ${f.width}×${f.height})` : 'Ready')}
                {f.status === 'error' && f.error}
              </span>
              <button type="button" className="rs-link" onClick={() => onRemove && onRemove(f.id)} aria-label={`Remove ${f.name}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const RECENT_STATE = { queued: 'Queued', reading: 'Reading', staging: 'Matching', done: 'Done', failed: 'Failed' };

export function RecentSheets({ sheets, onOpen }) {
  if (!sheets || !sheets.length) return null;
  return (
    <div className="rs-recent">
      <div className="rs-recent-title">Your recent sheets</div>
      <ul>
        {sheets.map((s) => {
          const step = progressStepOf({ status: s.status, staging: { status: s.stageStatus } });
          const when = s.createdAt ? new Date(s.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
          return (
            <li key={s.rosterId}>
              <button type="button" className="rs-recent-item" onClick={() => onOpen && onOpen(s.rosterId)}>
                <span>{s.facilityName || 'Group home'}</span>
                <span className="rs-muted">{when}{s.lineCount ? ` · ${s.lineCount} lines` : ''}</span>
                <span className="rs-recent-state">{s.discarded ? 'Discarded' : (RECENT_STATE[step] || '')}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// The JSON body POST /api/sms/chat/roster takes (uploadLimits.validatePageUpload).
export function uploadBody(facilityId, prepared) {
  const list = Array.isArray(prepared) ? prepared : [];
  const pdf = list.find((p) => p.kind === 'pdf');
  if (pdf) return { facilityId, pdfBase64: bytesToBase64(pdf.bytes) };
  return { facilityId, images: list.map((p) => ({ mimeType: p.mimeType, base64: bytesToBase64(p.bytes) })) };
}

export function RefillSheetDialog({ onClose, onUploaded, onOpenSheet, client = ccmApi, deps = null }) {
  const [facilities, setFacilities] = useState(null);
  const [facilityError, setFacilityError] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  // The server's own caps (GET /facilities `limits`), mirrored before anything is sent;
  // the ref is what a file being prepared reads.
  const [limits, setLimits] = useState(() => normalizeLimits(null));
  const limitsRef = useRef(limits);
  // A file added before the facilities answer waits for the served limits, so it is
  // prepared against the server's caps and never against the fallback.
  const limitsReady = useRef(null);
  if (!limitsReady.current) {
    let resolve;
    limitsReady.current = new Promise((r) => { resolve = r; });
    limitsReady.current.resolve = resolve;
  }
  const [recent, setRecent] = useState([]);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const headingRef = useRef(null);
  const prepDeps = useRef(deps);

  useEffect(() => {
    let live = true;
    client.listRosterFacilities()
      .then((r) => {
        if (!live) return;
        const list = (r && r.facilities) || [];
        const l = normalizeLimits(r && r.limits);
        limitsRef.current = l;
        setLimits(l);
        limitsReady.current.resolve(l);
        setFacilities(list);
        if (list.length === 1) setFacilityId(list[0].id);
      })
      .catch((e) => {
        limitsReady.current.resolve(limitsRef.current);
        if (live) { setFacilities([]); setFacilityError((e && e.message) || "The group homes couldn't be loaded."); }
      });
    client.listRecentRosterSheets()
      .then((r) => { if (live) setRecent((r && r.sheets) || []); })
      .catch(() => {});
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function addFiles(list) {
    setError(null);
    const added = list.map((file) => ({ id: `f${(fileSeq += 1)}`, name: file.name || 'pasted image', status: 'preparing', file }));
    setFiles((fs) => [...fs, ...added]);
    if (!prepDeps.current) prepDeps.current = domDeps();
    for (const entry of added) {
      limitsReady.current.then((l) => prepareFile(entry.file, prepDeps.current, l))
        .then((p) => setFiles((fs) => fs.map((f) => (f.id === entry.id
          ? { ...f, status: 'ready', kind: p.kind, prepared: p, resized: p.resized, width: p.width, height: p.height }
          : f))))
        .catch((e) => setFiles((fs) => fs.map((f) => (f.id === entry.id
          ? { ...f, status: 'error', error: (e && e.message) || "This file couldn't be read." }
          : f))));
    }
  }

  // Paste anywhere in the dialog: an image copied from Photos or a screenshot.
  function onPaste(e) {
    const items = e.clipboardData ? Array.from(e.clipboardData.files || []) : [];
    if (items.length) { e.preventDefault(); addFiles(items); }
  }

  // lib/refill-sheet uploadReadiness: a home, every page ready (a failed one blocks), the
  // selection and the total the server takes, no upload under way.
  const { canUpload, problem, ready } = uploadReadiness({ facilityId, files, limits, uploading });

  async function upload() {
    if (!canUpload) return;
    setUploading(true);
    setError(null);
    const facility = (facilities || []).find((f) => f.id === facilityId) || null;
    await performUpload({
      client,
      body: uploadBody(facilityId, ready.map((f) => f.prepared)),
      onError: (msg) => { setError(msg); setUploading(false); },
      onDone: (rosterId) => { if (onUploaded) onUploaded(rosterId, facility); },
    });
  }

  return (
    <SheetModal
      title="Refill sheet"
      subtitle="Each line is matched to a resident's chart. Confident lines become unsigned refill drafts in Elation; the rest wait for you."
      onClose={onClose}
      className="rs-dialog"
      labelId="rs-dialog-title"
      initialFocus={headingRef}
      footer={(
        <>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" data-action="upload-sheet" disabled={!canUpload} onClick={upload}>
            {uploading ? 'Uploading…' : 'Upload sheet'}
          </button>
        </>
      )}
    >
      <div onPaste={onPaste}>
        <div tabIndex={-1} ref={headingRef} className="rs-visually-hidden">Refill sheet upload</div>
        <FacilityPick
          facilities={facilities || []}
          loading={facilities === null}
          error={facilityError}
          value={facilityId}
          onChange={setFacilityId}
        />
        <SheetFiles
          files={files}
          onAdd={addFiles}
          onRemove={(id) => setFiles((fs) => fs.filter((f) => f.id !== id))}
          dragOver={dragOver}
          setDragOver={setDragOver}
          inputRef={inputRef}
          maxPages={limits.maxPages}
        />
        {problem && <div className="rs-error" role="alert">{problem}</div>}
        {error && <div className="rs-error" role="alert">{error}</div>}
        <RecentSheets sheets={recent} onOpen={(id) => onOpenSheet && onOpenSheet(id)} />
      </div>
    </SheetModal>
  );
}
